import { ReactNode } from 'react';

import { IconCalendar, IconPercent, IconPound, IconWallet } from '@iwoca/icons';

import styles from './FormalOfferCard.module.css';
import {
  getDurationInMonths,
  getProductName,
} from '../../helpers/product.helpers';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { TGetFormalOffersResponse } from '../../types';

type TProps = Pick<
  TGetFormalOffersResponse['data']['formal_offers'][number],
  | 'product_type'
  | 'duration'
  | 'max_amount'
  | 'interest_rates'
  | 'withdrawal_fee_percentage'
> & {
  isVariant?: boolean;
  // In the variant this is the LONGEST term across all offered durations
  // (the card shows "Up to X months"). Falls back to this offer's own duration.
  termInMonths?: number | null;
  // Optional: the generated type marks it required, but it is absent at runtime
  // for the control arm and for offers with no minimum_monthly_repayment.
  minimum_monthly_repayment?: number;
};

const ICON_SIZE = 20;
const ICON_PROPS = {
  width: ICON_SIZE,
  height: ICON_SIZE,
} as const;

// A single row of the redesigned (variant) offer card: an icon in a fixed
// left column, vertically centred against a stacked label (top) and value
// (bottom). On desktop the value moves to the right of the label; on mobile
// it stays stacked underneath (see the CSS media query).
const VariantDetailRow = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) => (
  <li className={styles.variantDetailsListItem}>
    <span className={styles.variantItemIcon}>{icon}</span>
    <span className={styles.variantRowText}>
      <span className={styles.variantLabel}>{label}</span>
      <span className={styles.variantDetailValue}>{value}</span>
    </span>
  </li>
);

const FormalOfferCard = ({
  product_type,
  duration,
  max_amount,
  interest_rates,
  withdrawal_fee_percentage,
  isVariant,
  termInMonths,
  minimum_monthly_repayment,
}: TProps) => {
  const { formatCurrency, formatPercentage } = useNumberFormat();

  // In the variant we show the longest term across all offered durations
  // ("Up to X months"); the control shows this offer's own duration unchanged.
  const months = isVariant
    ? (termInMonths ?? getDurationInMonths(duration))
    : getDurationInMonths(duration);
  const termLength =
    months === null ? 'N/A' : `${isVariant ? 'Up to ' : ''}${months} months`;

  // The variant uses the redesigned card: a centred product-name header above
  // icon + label/value rows (see VariantDetailRow). The control keeps the
  // existing two-column layout unchanged so it stays a clean A/B baseline.
  if (isVariant) {
    const hasMinMonthlyRepayment =
      minimum_monthly_repayment !== undefined &&
      minimum_monthly_repayment !== null;

    return (
      <div className={styles.variantContainer}>
        <h3 className={styles.variantHeading}>
          {getProductName(product_type)}
        </h3>
        <ul className={styles.variantDetailsList}>
          <VariantDetailRow
            icon={<IconCalendar data-testid="calendar-icon" {...ICON_PROPS} />}
            label="Term length"
            value={termLength}
          />
          <VariantDetailRow
            icon={<IconPound data-testid="pound-icon" {...ICON_PROPS} />}
            label="Offer amount"
            value={formatCurrency(max_amount)}
          />
          {hasMinMonthlyRepayment ? (
            <VariantDetailRow
              icon={
                <IconWallet
                  data-testid="min-monthly-repayment-icon"
                  {...ICON_PROPS}
                />
              }
              label="Min. monthly repayment"
              value={formatCurrency(minimum_monthly_repayment)}
            />
          ) : null}
          {withdrawal_fee_percentage ? (
            <VariantDetailRow
              icon={<IconPercent data-testid="percent-icon" {...ICON_PROPS} />}
              label="Withdrawal fee"
              value={formatPercentage(withdrawal_fee_percentage)}
            />
          ) : null}
        </ul>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.heading}>{getProductName(product_type)}</h3>
      <ul className={styles.detailsList}>
        <li className={styles.detailsListItem}>
          <IconCalendar
            data-testid="calendar-icon"
            width={ICON_SIZE}
            height={ICON_SIZE}
            className={styles.itemIcon}
          />
          Term length: <span className={styles.detailValue}>{termLength}</span>
        </li>
        <li className={styles.detailsListItem}>
          <IconPound
            data-testid="pound-icon"
            width={ICON_SIZE}
            height={ICON_SIZE}
            className={styles.itemIcon}
          />
          Offer amount:{' '}
          <span className={styles.detailValue}>
            {formatCurrency(max_amount)}
          </span>
        </li>
        <li className={styles.detailsListItem}>
          <IconPercent
            data-testid="percent-icon"
            width={ICON_SIZE}
            height={ICON_SIZE}
            className={styles.itemIcon}
          />
          Interest rate:{' '}
          <span className={styles.detailValue}>
            {formatPercentage(interest_rates[0].rate)}
          </span>
        </li>
        {withdrawal_fee_percentage ? (
          <li className={styles.detailsListItem}>
            <IconPercent
              data-testid="percent-icon"
              width={ICON_SIZE}
              height={ICON_SIZE}
              className={styles.itemIcon}
            />
            Withdrawal fee:{' '}
            <span className={styles.detailValue}>
              {formatPercentage(withdrawal_fee_percentage)}
            </span>
          </li>
        ) : null}
      </ul>
    </div>
  );
};
export default FormalOfferCard;
